import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";
const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

export default function ContactFormDialog({ open, onOpenChange, contact, types = [], onSaved }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("Other");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [clientIds, setClientIds] = useState([]);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    if (contact) {
      setName(contact.name || ""); setType(contact.type || "Other"); setEmail(contact.email || "");
      setPhone(contact.phone || ""); setCompany(contact.company || ""); setAddress(contact.address || "");
      setNotes(contact.notes || ""); setClientIds(contact.client_ids || []);
    } else {
      setName(""); setType(types[0] || "Other"); setEmail(""); setPhone("");
      setCompany(""); setAddress(""); setNotes(""); setClientIds([]);
    }
  }, [open, contact, types]);

  const toggleClient = (id) =>
    setClientIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    if (!name.trim()) { toast.error("Add a contact name"); return; }
    setSaving(true);
    const body = { name, type, email, phone, company, address, notes, client_ids: clientIds };
    try {
      if (contact) await api.put(`/contacts/${contact.id}`, body);
      else await api.post("/contacts", body);
      toast.success(contact ? "Contact updated" : "Contact added");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save contact");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="contact-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{contact ? "Edit contact" : "New contact"}</DialogTitle>
          <DialogDescription className="sr-only">Add or edit an address book contact.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className={labelCls}>Name</label>
            <input data-testid="contact-name-input" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="contact-type-trigger" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" data-testid="contact-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input data-testid="contact-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} />
          </div>
        </div>

        <label className={labelCls}>Company / Organization</label>
        <input data-testid="contact-company-input" value={company} onChange={(e) => setCompany(e.target.value)} className={inputCls + " mb-3"} />

        <label className={labelCls}>Address</label>
        <input data-testid="contact-address-input" value={address} onChange={(e) => setAddress(e.target.value)} className={inputCls + " mb-3"} />

        <label className={labelCls}>Notes</label>
        <textarea data-testid="contact-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls + " mb-3"} />

        <label className={labelCls}>Link to clients (optional)</label>
        <div className="border border-gray-200 rounded-sm max-h-32 overflow-y-auto p-1 mb-2" data-testid="contact-clients">
          {clients.length === 0 && <p className="text-xs text-gray-400 px-2 py-1">No clients available.</p>}
          {clients.map((c) => (
            <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded-sm cursor-pointer text-sm">
              <input type="checkbox" checked={clientIds.includes(c.id)} onChange={() => toggleClient(c.id)} className="accent-[#002FA7]" />
              <span className="text-gray-700">{c.name}</span>
            </label>
          ))}
        </div>

        <DialogFooter className="mt-2">
          <button onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="contact-save-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm disabled:opacity-60">
            {saving ? "Saving…" : contact ? "Save changes" : "Add contact"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
